import { useDataFetchOrchestrator } from "@/hooks/use-data-fetch-orchestrator";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { useSearchPageMapData } from "@/providers/search";
import { Bounds } from "@/providers/search/stores/map-store";
import { debounce } from "@/utils/debounce";
import { logger } from "@/utils/logger";
import { transformFilters } from "@/utils/search/transform-filters";
import { useMap } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useRef } from "react";

/**
 * Hook to handle automatic property fetching when map bounds change or filters change
 * Segregates filter changes (data fetching) from bounds changes (bounds handling)
 * Optimized for performance with minimal re-renders and efficient debouncing
 */
export function useMapBoundsUpdater() {
	const map = useMap();
	const setBounds = useSearchPageMapData(state => state.setBounds);
	const { filters, setFilters } = useUrlFilters();
	const mode = useSearchPageMapData(state => state.mode);
	const fetchMapData = useSearchPageMapData(state => state.fetchMapData);
	const fetchNearByProjects = useSearchPageMapData(state => state.fetchNearByProjects);
	const { orchestrateFetch } = useDataFetchOrchestrator({ mode, fetchMapData, fetchNearByProjects });
	const isFirstTime = useRef(true);

	const listenerRef = useRef<google.maps.MapsEventListener | null>(null);
	// Track if this is the first mount to handle initial mode properly
	const isMountedRef = useRef(false);
	const previousModeRef = useRef<"default" | "project">(mode);
	// Optimized bounds change handler with better performance
	const handleBoundsChanged = useCallback(() => {
		if (!map) return;

		// Use requestAnimationFrame to batch DOM reads and avoid forced reflows
		requestAnimationFrame(() => {
			const bounds = map.getBounds()?.toJSON() as Bounds;
			if (!bounds) return;

			// Fix invalid bounds by swapping if needed
			const validBounds: Bounds = {
				north: Math.max(bounds.north, bounds.south),
				south: Math.min(bounds.north, bounds.south),
				east: Math.max(bounds.east, bounds.west),
				west: Math.min(bounds.east, bounds.west),
			};

			if (
				validBounds.north !== bounds.north ||
				validBounds.south !== bounds.south ||
				validBounds.east !== bounds.east ||
				validBounds.west !== bounds.west
			) {
				logger.warn("[useMapBoundsUpdater] Fixed invalid bounds on first change", {
					original: bounds,
					fixed: validBounds,
				});
			}

			// Update bounds in store
			setBounds(validBounds);

			if (isFirstTime.current) {
				isFirstTime.current = false;

				const { filtersForGraphQL, newFilters } = transformFilters({
					filters,
					selectedLocationId: filters.selectedLocationId,
					mapBounds: validBounds,
					mapZoom: map.getZoom(),
					from: "postSSR",
				});

				setFilters(newFilters);
				// Pass isInitialFetch: true for the first post-SSR fetch to preserve SSR data
				orchestrateFetch({ filters: newFilters, map, filtersForGraphQL, isInitialFetch: true });
			}
		});
	}, [map, setBounds, filters, setFilters, orchestrateFetch]);

	// Trigger data fetch when mode changes (switching between project/default modes)
	useEffect(() => {
		// On first mount, set the flag and previousModeRef
		if (!isMountedRef.current) {
			isMountedRef.current = true;
			previousModeRef.current = mode;

			// IMPORTANT: If the component mounts with non-default mode (e.g., project mode from URL or mobile view switch),
			// we need to trigger a fetch immediately because the bounds_changed listener won't trigger a mode-specific fetch.
			// This handles the case where user switches to map view with project mode already set in URL.
			if (mode !== "default" && map) {
				const bounds = map.getBounds()?.toJSON() as Bounds;

				if (bounds) {
					// Fix invalid bounds by swapping if needed
					const validBounds: Bounds = {
						north: Math.max(bounds.north, bounds.south),
						south: Math.min(bounds.north, bounds.south),
						east: Math.max(bounds.east, bounds.west),
						west: Math.min(bounds.east, bounds.west),
					};

					if (
						validBounds.north !== bounds.north ||
						validBounds.south !== bounds.south ||
						validBounds.east !== bounds.east ||
						validBounds.west !== bounds.west
					) {
						logger.warn("[useMapBoundsUpdater] Fixed invalid bounds on mount", {
							original: bounds,
							fixed: validBounds,
						});
					}

					const { filtersForGraphQL } = transformFilters({
						filters,
						selectedLocationId: filters.selectedLocationId,
						mapBounds: validBounds,
						mapZoom: map.getZoom(),
						from: "postSSR",
					});
					// Fetch immediately with the current mode
					orchestrateFetch({ filters, map, filtersForGraphQL, isInitialFetch: false });
				} else {
					logger.warn("[useMapBoundsUpdater] No bounds available on mount with non-default mode");
				}
			}
			return;
		}

		// Only trigger if mode actually changed after mount
		if (mode !== previousModeRef.current) {
			previousModeRef.current = mode;

			if (map) {
				const bounds = map.getBounds()?.toJSON() as Bounds;

				if (bounds) {
					// Fix invalid bounds by swapping if needed
					const validBounds: Bounds = {
						north: Math.max(bounds.north, bounds.south),
						south: Math.min(bounds.north, bounds.south),
						east: Math.max(bounds.east, bounds.west),
						west: Math.min(bounds.east, bounds.west),
					};

					if (
						validBounds.north !== bounds.north ||
						validBounds.south !== bounds.south ||
						validBounds.east !== bounds.east ||
						validBounds.west !== bounds.west
					) {
						logger.warn("[useMapBoundsUpdater] Fixed invalid bounds for mode change", {
							original: bounds,
							fixed: validBounds,
						});
					}

					const { filtersForGraphQL } = transformFilters({
						filters,
						selectedLocationId: filters.selectedLocationId,
						mapBounds: validBounds,
						mapZoom: map.getZoom(),
						from: "postSSR",
					});

					orchestrateFetch({ filters, map, filtersForGraphQL, isInitialFetch: false });
				} else {
					logger.warn("[useMapBoundsUpdater] No bounds available for mode change");
				}
			} else {
				logger.warn("[useMapBoundsUpdater] No map available for mode change");
			}
		}
	}, [mode, map, filters, orchestrateFetch]);

	// Optimized effect to set up map bounds listener
	useEffect(() => {
		if (!map || listenerRef.current) {
			return;
		}

		const debouncedHandleBoundsChanged = debounce(handleBoundsChanged, 200);
		// Add bounds change listener
		const listener = map.addListener("bounds_changed", debouncedHandleBoundsChanged);
		listenerRef.current = listener;

		return () => {
			// Enhanced cleanup
			if (listenerRef.current) {
				listenerRef.current.remove();
				listenerRef.current = null;
			}
		};
	}, [map, handleBoundsChanged]);
}
